// Car models for both race modes. Every model is built at real scale (1 unit
// = 1 metre), faces +z, sits on y = 0, and takes a Livery.
import * as THREE from 'three'
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js'
import { buildClosed } from './closed'
import { buildFormula } from './formula'
import { buildSculpted } from './sculpted'
import type { Driver } from './driver'
import type { Livery } from './livery'

export type CarType = 'formula' | 'hypercar' | 'prototype' | 'gt' | 'rookie'

export type CarModel = {
  group: THREE.Group
  wheels: THREE.Object3D[] // spin around x
  lamps: THREE.MeshBasicMaterial[] // headlights, lit at night
  dispose: () => void
}

export function carTypeFor(mode: 'grand_prix' | 'endurance', carClass?: string): CarType {
  if (mode === 'grand_prix') return 'formula'
  return (['hypercar', 'prototype', 'gt', 'rookie'] as const).find((t) => t === carClass) ?? 'rookie'
}

const builders: Record<CarType, (livery: Livery, driver?: Driver) => CarModel> = {
  formula: (l, d) => buildFormula(l, d),
  hypercar: (l) => buildClosed('hypercar', l),
  prototype: (l) => buildClosed('prototype', l),
  gt: (l) => buildSculpted('gt', l),
  rookie: (l) => buildSculpted('rookie', l),
}

export function buildCarModel(type: CarType, livery: Livery, driver?: Driver): CarModel {
  const model = builders[type](livery, driver)
  const group = mergeByMaterial(model.group)
  const wheels: THREE.Object3D[] = []
  group.traverse((o) => o.userData.wheel && wheels.push(o))
  return { ...model, group, wheels }
}

// A car is built from ~100 small parts; drawing them one by one is too slow
// for 22 cars. Merge every part that shares a material into one mesh (the
// wheels separately, inside their spinning groups), keeping the look.
function mergeByMaterial(root: THREE.Group): THREE.Group {
  root.updateMatrixWorld(true)
  const inverse = new THREE.Matrix4()
  const collect = (node: THREE.Object3D, base: THREE.Object3D, skipWheels: boolean) => {
    inverse.copy(base.matrixWorld).invert()
    const buckets = new Map<THREE.Material, THREE.BufferGeometry[]>()
    node.traverse((o) => {
      if (skipWheels && o !== node && o.userData.wheel) return
      let parent = o.parent
      while (skipWheels && parent && parent !== node) {
        if (parent.userData.wheel) return
        parent = parent.parent
      }
      const mesh = o as THREE.Mesh
      if (!mesh.isMesh) return
      const material = mesh.material as THREE.Material
      let g = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone()
      for (const name of Object.keys(g.attributes)) if (!['position', 'normal', 'uv'].includes(name)) g.deleteAttribute(name)
      if (!g.attributes.normal) g.computeVertexNormals()
      if (!g.attributes.uv) g.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(g.attributes.position.count * 2), 2))
      g = g.applyMatrix4(new THREE.Matrix4().multiplyMatrices(inverse, mesh.matrixWorld))
      const list = buckets.get(material) ?? []
      list.push(g)
      buckets.set(material, list)
    })
    const out = new THREE.Group()
    for (const [material, list] of buckets) {
      const merged = mergeGeometries(list, false)
      list.forEach((g) => g.dispose())
      if (!merged) continue
      const mesh = new THREE.Mesh(merged, material)
      mesh.castShadow = true
      out.add(mesh)
    }
    return out
  }

  const result = collect(root, root, true)
  // Wheels: keep pivot (position) and spin (rotation) groups, merge inside.
  root.traverse((o) => {
    if (!o.userData.wheel) return
    const pivot = new THREE.Group()
    pivot.position.copy(o.parent!.position)
    const spin = collect(o, o, false)
    spin.userData.wheel = true
    pivot.add(spin)
    result.add(pivot)
  })
  return result
}
